// src/components/navigation/DesktopSidebar.tsx

import React from "react";
import { Link } from "react-router-dom";

import type { Notification } from "../../contexts/NotificationContext";
import type {
  NavItem,
  UserDisplayInfo,
} from "../../types/navbar.types";

import { GradientText } from "./GradientText";
import { NotificationDropdown } from "./NotificationDropdown";
import UserAvatar from "./UserAvatar";

interface DesktopSidebarProps {
  navItems: NavItem[];
  notifications: Notification[];
  unreadCount: number;
  userDisplayInfo: UserDisplayInfo;
  currentPath: string;
  isActive: (path: string) => boolean;
  onNavigate: (path: string) => void;
  onNotificationClick: (id: string) => void;
  onMarkAllAsRead: () => void;
  onViewAllNotifications: () => void;
}

const HIDDEN_LABELS = new Set(["Settings"]);

interface NavRowProps {
  item: NavItem;
  active: boolean;
  onNavigate: (path: string) => void;
}

const NavRow: React.FC<NavRowProps> = ({
  item,
  active,
  onNavigate,
}) => {
  return (
    <button
      type="button"
      onClick={() => onNavigate(item.path)}
      aria-current={active ? "page" : undefined}
      className={[
        "flex h-10 w-full items-center gap-3 rounded-md px-3",
        "text-sm font-medium transition-colors",
        "focus-visible:outline-none",
        "focus-visible:ring-2 focus-visible:ring-[#159A92]",
        "focus-visible:ring-offset-2",
        active
          ? "bg-[#F0F8F7] text-[#17313A]"
          : "text-[#52666C] hover:bg-[#F7FAF9] hover:text-[#17313A]",
      ].join(" ")}
    >
      <item.icon
        className={[
          "h-[18px] w-[18px] shrink-0",
          active
            ? "text-[#159A92]"
            : "text-[#7D9094]",
        ].join(" ")}
        aria-hidden="true"
      />

      <span className="truncate">{item.label}</span>
    </button>
  );
};

export const DesktopSidebar: React.FC<
  DesktopSidebarProps
> = ({
  navItems,
  notifications,
  unreadCount,
  userDisplayInfo,
  currentPath,
  isActive,
  onNavigate,
  onNotificationClick,
  onMarkAllAsRead,
  onViewAllNotifications,
}) => {
  const visibleNavItems = React.useMemo(
    () =>
      navItems.filter(
        (item) => !HIDDEN_LABELS.has(item.label)
      ),
    [navItems]
  );

  const profileIsActive =
    currentPath === "/profile" ||
    currentPath.startsWith("/profile/");

  return (
    <nav
      aria-label="Main navigation"
      className="
        fixed left-0 top-0 z-30 hidden h-screen w-56
        flex-col border-r border-[#E4EFED]
        bg-white md:flex
      "
    >
      <div className="px-5 pb-6 pt-6">
        <Link
          to="/dashboard"
          className="flex items-center gap-3"
          aria-label="GluviaCare+ dashboard"
        >
          <img
            src="/IMG/Logo_Icon.PNG"
            alt=""
            className="h-9 w-9 shrink-0 object-contain"
            loading="eager"
            onError={(event) => {
              event.currentTarget.style.display = "none";
            }}
          />

          <GradientText className="truncate text-lg font-semibold leading-none">
            GluviaCare+
          </GradientText>
        </Link>
      </div>

      <div className="flex-1 overflow-y-auto px-3">
        <div className="space-y-1">
          {visibleNavItems.map((item) => (
            <NavRow
              key={item.path}
              item={item}
              active={isActive(item.path)}
              onNavigate={onNavigate}
            />
          ))}

          <NotificationDropdown
            notifications={notifications}
            unreadCount={unreadCount}
            onNotificationClick={onNotificationClick}
            onMarkAllAsRead={onMarkAllAsRead}
            onViewAll={onViewAllNotifications}
            isActive={currentPath === "/notifications"}
            variant="desktop"
          />
        </div>
      </div>

      <div className="border-t border-[#E4EFED] p-3">
        <button
          type="button"
          onClick={() => onNavigate("/profile")}
          aria-current={
            profileIsActive ? "page" : undefined
          }
          className={[
            "flex w-full items-center gap-3 rounded-md p-2",
            "text-left transition-colors",
            "focus-visible:outline-none",
            "focus-visible:ring-2 focus-visible:ring-[#159A92]",
            "focus-visible:ring-offset-2",
            profileIsActive
              ? "bg-[#F0F8F7]"
              : "hover:bg-[#F7FAF9]",
          ].join(" ")}
        >
          <UserAvatar
            avatar={userDisplayInfo.avatar}
            initials={userDisplayInfo.initials}
            displayName={userDisplayInfo.displayName}
            size="md"
          />

          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-[#17313A]">
              {userDisplayInfo.displayName}
            </p>

            <p className="truncate text-xs text-[#65777C]">
              Facility profile
            </p>
          </div>
        </button>
      </div>
    </nav>
  );
};

export default DesktopSidebar;
